import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigation } from '../../Navigation';

/**
 * ThemePreviewPanel Component
 *
 * Shows a live preview of how themes will look when applied.
 * Displays in the lower half of the page when preview mode is enabled.
 */
const ThemePreviewPanel = ({ themes, previewMode, onClosePreview }) => {
  const { isExpanded } = useNavigation();

  if (!previewMode) return null;

  const activeTheme = themes?.find(theme => theme.is_active) || themes?.[0];

  // Calculate sidebar width based on expanded/collapsed state
  const sidebarWidth = isExpanded ? 280 : 64;

  if (!activeTheme) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: `${sidebarWidth}px` }, // Account for dynamic sidebar width
          right: 0,
          height: '50vh',
          zIndex: 1000,
          p: 3,
          overflow: 'auto',
          ml: { xs: 0, sm: 0 }, // No margin on mobile, sidebar handles spacing
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Theme Preview</Typography>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClosePreview}
            size="small"
          >
            Close Preview
          </Button>
        </Box>
        <Typography color="textSecondary">
          No themes available for preview.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: 0, sm: `${sidebarWidth}px` }, // Account for dynamic sidebar width
        right: 0,
        height: '50vh',
        zIndex: 1000,
        p: 3,
        overflow: 'auto',
        backgroundColor: activeTheme.background_color || '#ffffff',
        color: activeTheme.text_color || '#000000',
        fontFamily: activeTheme.font_family || 'Arial',
        fontSize: activeTheme.font_size || '14px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ color: activeTheme.primary_color }}>
          Theme Preview - {activeTheme.theme_name}
        </Typography>
        <Button
          startIcon={<CloseIcon />}
          onClick={onClosePreview}
          size="small"
          sx={{ color: activeTheme.primary_color }}
        >
          Close Preview
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Color Palette Preview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ backgroundColor: activeTheme.background_color }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Color Palette
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label="Primary"
                  sx={{
                    backgroundColor: activeTheme.primary_color,
                    color: '#ffffff',
                  }}
                />
                <Chip
                  label="Secondary"
                  sx={{
                    backgroundColor: activeTheme.secondary_color,
                    color: '#ffffff',
                  }}
                />
                <Chip
                  label="Accent"
                  sx={{
                    backgroundColor: activeTheme.accent_color,
                    color: '#ffffff',
                  }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2">
                Background: {activeTheme.background_color}
              </Typography>
              <Typography variant="body2">
                Text: {activeTheme.text_color}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Typography Preview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ backgroundColor: activeTheme.background_color }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Typography Preview
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  color: activeTheme.primary_color,
                  fontFamily: activeTheme.font_family,
                }}
              >
                Heading 1
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: activeTheme.secondary_color,
                  fontFamily: activeTheme.font_family,
                }}
              >
                Heading 2
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: activeTheme.text_color,
                  fontFamily: activeTheme.font_family,
                  fontSize: activeTheme.font_size,
                }}
              >
                This is how body text will look with your theme applied.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Custom CSS Preview */}
        {activeTheme.custom_css && (
          <Grid size={{ xs: 12 }}>
            <Card sx={{ backgroundColor: activeTheme.background_color }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Custom CSS Applied
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }}
                >
                  <pre>{activeTheme.custom_css}</pre>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default ThemePreviewPanel;
